import React, { FC, useMemo, useState } from 'react';
import { Trash, Edit } from '@td-client-frontend/icons';
import { MarketingComment } from '../../../../../hooks/useMarketingComments';
import { format, parseISO } from 'date-fns';
import { useAuth } from '../../../../../context/AuthContext';
import { PermMode } from '../../../../WithPermission';
import { Nullable } from '../../../../../@types/common';
import Modal from '../../../../Modal/NewModal';
import CommentsTextArea from './CommentsTextArea';
import { Permissions } from '../../../../../@types/Permissions';

interface MarketingCommentItem {
  marketingComment: MarketingComment;
  onDelete: (profileId: string) => void;
  onChange: (marketingCommentValue: string) => void;
  setCommentToEdit: (marketingComment: Nullable<MarketingComment>) => void;
  commentToEdit: Nullable<MarketingComment>;
}

const MarketingCommentItem: FC<MarketingCommentItem> = ({
  marketingComment,
  onDelete,
  onChange,
  setCommentToEdit,
  commentToEdit
}) => {
  const {
    authState: { userInfo },
    checkPerm
  } = useAuth();

  const [commentToDelete, setCommentToDelete] = useState<Nullable<MarketingComment>>(null);

  const showDeleteProfileModal = () => setCommentToDelete(marketingComment);

  const onCloseDeleteModalHandler = () => setCommentToDelete(null);

  const deleteProfileHandler = () => {
    if (commentToDelete?.id) {
      onDelete(commentToDelete?.id);
      onCloseDeleteModalHandler();
    }
  };

  const onEditTextAreaClose = () => {
    setCommentToEdit(null);
  };

  const onEditCommentHandler = () => {
    setCommentToEdit(marketingComment);
  };

  const canEdit: boolean = useMemo(() => {
    const hasWriteAndCreate = checkPerm(
      [Permissions.MarketingProfileCommentsWrite, Permissions.MarketingProfileCommentsCreate],
      PermMode.ALL
    );
    const hasWrite = checkPerm([Permissions.MarketingProfileCommentsWrite], PermMode.ALL);
    const hasCreate = checkPerm([Permissions.MarketingProfileCommentsCreate], PermMode.ALL);

    return (
      hasWriteAndCreate ||
      (hasWrite && marketingComment.by.id === userInfo?.uid?.toString()) ||
      (hasCreate && !hasWrite && marketingComment.by.id !== userInfo?.uid?.toString())
    );
  }, [checkPerm, marketingComment.by.id, userInfo]);

  return (
    <>
      <Modal
        isOpen={!!commentToDelete?.id}
        onClose={onCloseDeleteModalHandler}
        header="Delete comment"
        actions={[
          { title: 'Cancel', kind: 'secondary' },
          {
            title: 'Delete',
            kind: 'primary',
            onClick: deleteProfileHandler
          }
        ]}
        size="small"
      >
        <div className="px-6 py-4 font-effra h-36" data-testid="delete-comment-modal">
          <h2 className="text-xl text-gray-45">Are sure you want to delete this comment?</h2>
        </div>
      </Modal>
      <div className="flex flex-col pt-2 pb-6 border-b-1 border-grey-20" data-testid="marketing-comment-item">
        <div className="flex justify-between">
          <p className="font-effra text-gray-60 text-lg flex items-center" data-testid="marketing-comment-label">
            <span className="text-gray-45">{marketingComment.by.fullName}</span>
            &nbsp; - &nbsp;
            {format(parseISO(marketingComment.createdAt), 'MM/dd/yyyy hh:mma')}
          </p>
          {canEdit && (
            <div className="flex space-x-2" data-testid="marketing-comment-item-buttons">
              <button onClick={onEditCommentHandler} data-testid="marketing-comment-item-buttons-edit">
                <Edit data-testid="marketing-comment-item-buttons-edit-icon" />
              </button>
              <button onClick={showDeleteProfileModal} data-testid="marketing-comment-item-buttons-delete">
                <Trash
                  className="fill-current text-primary-500 w-10"
                  data-testid="marketing-comment-item-buttons-delete-icon"
                />
              </button>
            </div>
          )}
        </div>
        {commentToEdit?.id === marketingComment.id ? (
          <CommentsTextArea
            rightButtonLabel="Update comment"
            textAreaName="edit-marketing-comment"
            marketingComment={commentToEdit}
            onChange={onChange}
            onClose={onEditTextAreaClose}
          />
        ) : (
          <p className="w-full break-words">{marketingComment.comment}</p>
        )}
      </div>
    </>
  );
};

export default MarketingCommentItem;
